import { BREAKPOINTS } from "@/constants";
import { Breakpoints, Style } from "@/types";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

// Merge class names safely with Tailwind merge
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Convert style object to inline style string
export function mapStyles(style: Style): string {
  /* eslint-disable @typescript-eslint/no-unused-vars */
  return Object.entries(style)
    .filter(([_, v]) => typeof v !== "object" && v != null)
    .map(([k, v]) => `${k}: ${v};`)
    .join(" ");
}
// Change the current theme on the document
export function changeTheme(theme: "light" | "dark") {
  document.documentElement.setAttribute("data-theme", theme);
}

// Initialize theme to light if not set
export function initTheme() {
  const currentTheme = document.documentElement.getAttribute("data-theme");
  if (!currentTheme || (currentTheme !== "light" && currentTheme !== "dark")) {
    changeTheme("light");
  }
}

// Get current theme
export function getTheme(): "light" | "dark" {
  return document.documentElement.getAttribute("data-theme")! as
    | "light"
    | "dark";
}

// Resolve image URL based on theme
export function resolveImageUrl(
  url: string,
  theme: "light" | "dark" | string,
): string {
  return url.includes("{{theme}}") ? url.replace("{{theme}}", theme) : url;
}

// Splits a Style object into base and responsive
export function splitStyle(style: Style) {
  const base: Style = {};
  const responsive: Record<Breakpoints, Style> = {} as any; // eslint-disable-line @typescript-eslint/no-explicit-any

  for (const key in style) {
    const value = style[key];
    if (value == null) continue;

    if (key in BREAKPOINTS && typeof value === "object") {
      responsive[key as Breakpoints] = value as Style;
    } else {
      base[key] = value as string | number;
    }
  }

  return { base, responsive };
}

// utils/layoutUtils.ts
export function nextPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

/**
 * Updates flex widths of buttons:
 * - Last row normally fixed width
 * - Other rows flex-grow: 1
 * - If last row === first row, last row grows instead
 */
// export async function updateSocialLoginBtnWidths(buttons: HTMLElement[]) {
//   if (!buttons.length) return;

//   await nextPaint(); // wait for layout to settle

//   // Detect first row
//   let firstTop: number | null = null;
//   const firstRow: HTMLElement[] = [];
//   for (const btn of buttons) {
//     const { top } = btn.getBoundingClientRect();
//     if (firstTop === null) firstTop = top;
//     if (top !== firstTop) break;
//     firstRow.push(btn);
//   }

//   if (!firstRow.length) return;

//   const rectWidth = firstRow[0].getBoundingClientRect().width;
//   const width = `${rectWidth}px`;

//   const isSingleRow = firstRow.length === buttons.length; // all buttons are in the first row

//   // Apply width to all rows
//   buttons.forEach((b, i) => {
//     if (isSingleRow) {
//       b.style.flexBasis = "0";
//       b.style.flexGrow = "1"; // all buttons grow if single row
//     } else {
//       b.style.flexGrow = "0";
//       b.style.flexBasis = width;
//     }
//   });

//   await nextPaint(); // ensure the browser repaints with updated flex-basis
// }

// export async function updateSocialLoginBtnWidths(
//   container: HTMLElement,
//   buttons: HTMLElement[]
// ) {
//   if (!buttons.length) return;

//   await nextPaint();

//   const containerWidth = container.offsetWidth;
//   const buttonCount = buttons.length;
//   const minButtonWidth = 50; // px
//   const gap = 8; // px, must match --sso-gap (0.5rem)

//   // Compute max number of buttons that can fit in one row (considering gaps)
//   let cols = Math.floor((containerWidth + gap) / (minButtonWidth + gap));
//   cols = Math.max(1, Math.min(cols, buttonCount)); // at least 1, at most total buttons

//   // Set CSS vars
//   container.style.setProperty("--sso-cols", String(cols));
//   container.style.setProperty("--sso-gap", `${gap}px`);
//   container.style.setProperty(
//     "--sso-item-width",
//     `calc(100% / var(--sso-cols) - var(--sso-gap) / var(--sso-cols) * (var(--sso-cols) - 1))`
//   );

//   console.log(
//     `[updateSocialLoginBtnWidths] container=${containerWidth}px, cols=${cols}, totalButtons=${buttonCount}`
//   );
// }

export async function updateSocialLoginBtnWidths(
  container: HTMLElement,
  buttons: HTMLElement[],
) {
  if (!buttons.length) return;

  await nextPaint();

  const containerWidth = container.offsetWidth;
  const buttonCount = buttons.length;
  const minButtonWidth = 60; // px
  const gap = 8; // px

  let bestCols = 1;
  let bestItemWidth = 0;
  let bestPriority = -1; // higher is better

  for (let cols = 1; cols <= buttonCount; cols++) {
    const totalGap = gap * (cols - 1);
    const itemWidth = (containerWidth - totalGap) / cols;
    const lastRow = buttonCount % cols;

    if (itemWidth < minButtonWidth) continue; // too small, skip

    // Determine priority
    let priority = 0;
    if (lastRow === 0 && cols > bestCols)
      priority = bestPriority + 1; // full rows → best
    else if (lastRow >= 2)
      priority = 2; // last row ≥ 2 → good
    else continue; // lastRow = 1 → invalid

    // Choose if higher priority OR same priority but larger item width
    if (
      priority > bestPriority ||
      (priority === bestPriority && itemWidth > bestItemWidth)
    ) {
      bestCols = cols;
      bestItemWidth = itemWidth;
      bestPriority = priority;
    }
  }

  // Set CSS vars
  container.style.setProperty("--sso-cols", String(bestCols));
  container.style.setProperty("--sso-gap", `${gap}px`);
  container.style.setProperty(
    "--sso-item-width",
    `calc(100% / var(--sso-cols) - var(--sso-gap) / var(--sso-cols) * (var(--sso-cols) - 1))`,
  );
}

export function deepMerge<
  T extends Record<string, any>,
  U extends Record<string, any>,
>(target: T, source: U): T & U {
  const result: any = { ...target };

  for (const key in source) {
    const sourceVal = source[key];
    const targetVal = result[key];

    if (
      sourceVal &&
      typeof sourceVal === "object" &&
      !Array.isArray(sourceVal)
    ) {
      result[key] = deepMerge(
        (targetVal ?? {}) as Record<string, any>,
        sourceVal as Record<string, any>,
      );
    } else {
      result[key] = sourceVal;
    }
  }

  return result as T & U;
}
